import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, Plus } from "lucide-react";
import { SERVICES, WHATSAPP_NUMBER } from "@/data/site";
import PayDialog from "@/components/PayDialog";

const EASE = [0.16, 1, 0.3, 1];

export default function BudgetCalculator() {
  const [selected, setSelected] = useState(["community-manager"]);
  const [payOpen, setPayOpen] = useState(false);

  const toggle = (id) =>
    setSelected((prev) => (prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]));

  const total = SERVICES.filter((s) => selected.includes(s.id)).reduce((acc, s) => acc + s.price, 0);
  const names = SERVICES.filter((s) => selected.includes(s.id)).map((s) => s.name);
  const waLink = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
    `Hola Socify, me interesan: ${names.join(", ") || "vuestros servicios"}. Estimación web: ${total} €. ¿Me contáis más?`
  )}`;

  return (
    <section id="presupuesto" className="relative overflow-hidden bg-slate-950 py-24 sm:py-32">
      <div aria-hidden className="absolute -top-32 right-0 h-96 w-96 rounded-full bg-[#1D4ED8]/30 blur-[140px]" />
      <div aria-hidden className="absolute bottom-0 left-0 h-72 w-72 rounded-full bg-[#00F0FF]/10 blur-[120px]" />

      <div className="relative mx-auto max-w-7xl px-6">
        <div className="grid grid-cols-1 gap-16 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <p className="mb-4 font-mono-sig text-xs font-bold uppercase tracking-[0.3em] text-[#00F0FF]">
              ( 04 ) — Presupuesto
            </p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white">
              Calcula tu inversión en 10 segundos.
            </h2>
            <p className="mt-6 max-w-md text-base leading-relaxed text-slate-400">
              Selecciona los servicios que necesita tu negocio y obtén una estimación al instante.
              Sin registros, sin compromiso.
            </p>

            <div className="mt-12 rounded-2xl border border-slate-800 bg-slate-900/60 p-8 backdrop-blur">
              <p className="font-mono-sig text-[11px] uppercase tracking-[0.25em] text-slate-500">
                Estimación desde
              </p>
              <div className="mt-2 flex items-baseline gap-2">
                <AnimatePresence mode="popLayout">
                  <motion.span
                    key={total}
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: -20, opacity: 0 }}
                    transition={{ duration: 0.4, ease: EASE }}
                    data-testid="budget-total"
                    className="font-display text-6xl font-extrabold text-[#00F0FF]"
                  >
                    {total} €
                  </motion.span>
                </AnimatePresence>
              </div>
              <p data-testid="budget-selected-count" className="mt-2 text-sm text-slate-500">
                {selected.length === 0
                  ? "Selecciona al menos un servicio"
                  : `${selected.length} servicio${selected.length > 1 ? "s" : ""} seleccionado${selected.length > 1 ? "s" : ""}`}
              </p>
              <a
                data-testid="budget-whatsapp-cta"
                href={waLink}
                target="_blank"
                rel="noopener noreferrer"
                className={`mt-8 flex w-full items-center justify-center gap-2.5 rounded-full py-4 text-sm font-bold transition-transform hover:scale-[1.02] active:scale-95 ${
                  selected.length === 0
                    ? "pointer-events-none bg-slate-800 text-slate-600"
                    : "bg-[#25D366] text-white shadow-xl shadow-[#25D366]/20"
                }`}
              >
                <MessageCircle className="h-4 w-4" />
                Pedir presupuesto por WhatsApp
              </a>
              <button
                data-testid="budget-paypal-cta"
                onClick={() => setPayOpen(true)}
                disabled={selected.length === 0}
                className={`mt-3 flex w-full items-center justify-center gap-2.5 rounded-full border py-4 text-sm font-bold transition-all hover:scale-[1.02] active:scale-95 ${
                  selected.length === 0
                    ? "pointer-events-none border-slate-800 text-slate-600"
                    : "border-[#00F0FF]/60 text-[#00F0FF] hover:bg-[#00F0FF]/10"
                }`}
              >
                Pagar señal con PayPal
              </button>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="flex flex-col gap-3">
              {SERVICES.map((s, i) => {
                const active = selected.includes(s.id);
                return (
                  <motion.button
                    key={s.id}
                    data-testid={`budget-calculator-checkbox-${s.id}`}
                    initial={{ opacity: 0, x: 40 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 0.7, ease: EASE, delay: i * 0.07 }}
                    onClick={() => toggle(s.id)}
                    className={`group flex items-center justify-between gap-4 rounded-2xl border px-6 py-5 text-left transition-all duration-300 ${
                      active
                        ? "border-[#00F0FF] bg-[#00F0FF]/10"
                        : "border-slate-800 bg-slate-900/40 hover:border-slate-600"
                    }`}
                  >
                    <div className="flex items-center gap-4">
                      <span
                        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full border transition-all duration-300 ${
                          active ? "border-[#00F0FF] bg-[#00F0FF] text-slate-950" : "border-slate-700 text-slate-500"
                        }`}
                      >
                        <Plus className={`h-4 w-4 transition-transform duration-300 ${active ? "rotate-45" : ""}`} />
                      </span>
                      <div>
                        <p className={`font-display text-lg font-bold ${active ? "text-white" : "text-slate-300"}`}>
                          {s.name}
                        </p>
                        <p className="text-xs text-slate-500">{s.tagline}</p>
                      </div>
                    </div>
                    <span className={`font-mono-sig text-sm font-bold ${active ? "text-[#00F0FF]" : "text-slate-500"}`}>
                      {s.priceLabel}
                    </span>
                  </motion.button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <PayDialog
        open={payOpen}
        onOpenChange={setPayOpen}
        defaultAmount={total || 50}
        description={`Señal · ${names.join(", ") || "Servicios Socify"} — Socify Marketing`}
      />
    </section>
  );
}
